import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import { setupGlobalGuards } from './guards.js'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
      meta: {
        title: '首页'
      }
    },
    {
      path: '/about',
      name: 'about',
      component: () => import('../views/AboutView.vue'),
      meta: {
        title: '关于我们'
      }
    },
    {
      path: '/components',
      name: 'components',
      component: () => import('../views/ComponentsView.vue'),
      meta: {
        title: '组件展示'
      }
    },
    // 认证相关路由
    {
      path: '/auth',
      name: 'auth',
      component: () => import('../views/auth/AuthView.vue'),
      meta: {
        title: '登录注册',
        requiresGuest: true
      }
    },
    {
      path: '/login',
      redirect: '/auth'
    },
    {
      path: '/register',
      redirect: '/auth'
    },
    // 控制台布局路由 - 需要登录即可访问
    {
      path: '/dashboard',
      component: () => import('../layouts/DashboardLayout.vue'),
      meta: { 
        requiresAuth: true,
        roles: [1, 2, 3] // 管理员、老师、学生都可访问
      },
      children: [
        {
          path: '',
          name: 'dashboard',
          component: () => import('../views/dashboard/DashboardView.vue'),
          meta: { 
            title: '控制台',
            roles: [1, 2]
          }
        },

        {
          path: '/homeworks',
          name: 'homeworks',
          component: () => import('../views/homework/HomeworkList.vue'),
          meta: { 
            title: '作业管理',
            roles: [1, 2, 3] 
          }
        },
        {
          path: '/homeworks/create',
          name: 'homework-create',
          component: () => import('../views/homework/HomeworkCreate.vue'),
          meta: { 
            title: '发布作业',
            roles: [1, 2] // 管理员和教师可创建
          }
        },
        {
          path: '/homeworks/:id/edit',
          name: 'homework-edit',
          component: () => import('../views/homework/HomeworkCreate.vue'),
          meta: { 
            title: '编辑作业',
            roles: [1, 2] // 管理员和教师可编辑
          }
        },
        {
          path: '/homeworks/:id',
          name: 'homework-detail',
          component: () => import('../views/homework/HomeworkDetail.vue'),
          meta: { 
            title: '作业详情',
            roles: [1, 2, 3] 
          }
        },
        {
          path: '/homeworks/:id/manage',
          name: 'homework-manage',
          component: () => import('../views/homework/HomeworkManage.vue'),
          meta: { title: '管理作业', roles: [1, 2] }
        },
        {
          path: '/homeworks/:id/report/:subId?',
          name: 'homework-report',
          component: () => import('../views/homework/HomeworkReport.vue'),
          meta: { title: '查看分析报告', roles: [1, 2, 3] }
        },
        {
          path: '/homeworks/:id/upload',
          name: 'homework-upload',
          component: () => import('../views/homework/HomeworkUpload.vue'),
          meta: { 
            title: '上传作业视频',
            roles: [1, 2, 3] 
          }
        },
        {
          path: '/users',
          name: 'users',
          component: () => import('../views/users/UsersView.vue'),
          meta: { title: '用户管理', roles: [1, 2] } // 允许管理员和教师访问
        },
        {
          path: '/settings',
          name: 'settings',
          component: () => import('../views/settings/SettingsView.vue'),
          meta: { 
            title: '系统设置',
            requiresAdmin: true
          }
        },
        {
          path: '/settings/profile',
          name: 'profile-settings',
          component: () => import('../views/user/ProfileView.vue'),
          meta: { 
            title: '个人设置',
            roles: [1, 2, 3]
          }
        },
        {
          path: '/settings/password',
          name: 'password-settings',
          component: () => import('../views/user/ChangePasswordView.vue'),
          meta: { 
            title: '修改密码',
            roles: [1, 2, 3]
          }
        },
        {
          path: '/admin/user/:userId',
          name: 'UserDetail',
          component: () => import('../views/admin/UserDetail.vue'),
          meta: {
            title: '用户详情',
            requiresAdmin: true
          }
        },
        {
          path: '/contact',
          name: 'contact',
          component: () => import('../views/contact/ContactView.vue'),
          meta: {
            title: '联系我们',
            roles: [1, 2, 3]
          }
        },
        {
          path: '/tools/analysis-task-console',
          name: 'analysis-task-console',
          component: () => import('../views/tools/AnalysisTaskConsole.vue'),
          meta: {
            title: '分析任务控制台',
            roles: [1, 2]
          }
        },
        {
          path: '/tools/analysis-notification-console',
          name: 'analysis-notification-console',
          component: () => import('../views/tools/AnalysisNotificationConsole.vue'),
          meta: {
            title: '分析完成邮件通知',
            roles: [1, 2]
          }
        },
        {
          path: '/tools/manual-homework-upload',
          name: 'manual-homework-upload',
          component: () => import('../views/tools/ManualHomeworkUpload.vue'),
          meta: {
            title: '代学生提交作业',
            roles: [1, 2]
          }
        },
        {
          path: '/tools/vision-analysis-workbench',
          name: 'vision-analysis-workbench',
          component: () => import('../views/tools/VisionAnalysisWorkbench.vue'),
          meta: {
            title: '视觉分析工作台',
            roles: [1, 2]
          }
        }
        /*
        {
          path: '/vision-detection',
          name: 'VisionDetection',
          component: () => import('../components/VisionDetectionComponent.vue'),
          meta: {
            title: '多模态情感分析系统',
            roles: [1]
          }
        },
        {
          path: '/audio-analysis',
          name: 'audio-analysis',
          component: () => import('../views/tools/AudioAnalysis.vue'),
          meta: {
            title: '合唱音准节奏分析',
            roles: [1, 2, 3]
          }
        }
        */
      ]
    },
    // 账户待审核页面
    {
      path: '/account-pending',
      name: 'account-pending',
      component: () => import('../views/AccountPending.vue'),
      meta: {
        title: '账户待审核'
      }
    },
    // 错误页面
    {
      path: '/403',
      name: 'forbidden',
      component: () => import('../views/error/403View.vue'),
      meta: {
        title: '权限不足'
      }
    },
    {
      path: '/404',
      name: 'notFound',
      component: () => import('../views/error/404View.vue'),
      meta: {
        title: '页面未找到'
      }
    },
    // 捕获所有未匹配的路由
    {
      path: '/:pathMatch(.*)*',
      redirect: '/404'
    }
  ]
})

// 设置全局路由守卫
setupGlobalGuards(router)

export default router
